import { useState } from 'react';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import { darkPrimary, darkGhost } from './components/primitives/DarkHeader';
import Card from './components/primitives/Card';
import Button from './components/primitives/Button';
import Badge from './components/primitives/Badge';
import Avatar from './components/primitives/Avatar';
import EmptyState from './components/primitives/EmptyState';
import FormInput from './components/primitives/FormInput';

function ThemedSelect({ theme, label, value, onChange, children }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: NEUTRAL.text, marginBottom: '4px' }}>{label}</label>
      )}
      <select value={value} onChange={onChange} style={{
        width: '100%', padding: '9px 12px', borderRadius: '7px',
        border: `1px solid ${theme.card_border}`, fontSize: '0.9em',
        background: 'white', outline: 'none', boxSizing: 'border-box',
      }}>
        {children}
      </select>
    </div>
  );
}

// ─── Inline-formulär: Lägg till block ────────────────────────────────────────
function AddBlockForm({ programId, sortOrder, suggestedStartWeek, theme, onCancel, onAdded }) {
  const [title, setTitle] = useState('');
  const [startWeek, setStartWeek] = useState(String(suggestedStartWeek));
  const [endWeek, setEndWeek] = useState(String(suggestedStartWeek + 1));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('program_blocks').insert({
      program_id: programId,
      title: title.trim(),
      start_week: Number(startWeek),
      end_week: Number(endWeek),
      sort_order: sortOrder,
    });
    setSaving(false);
    if (error) showToast('Kunde inte skapa block: ' + error.message, 'error');
    else onAdded();
  };

  return (
    <div style={{
      border: `1px solid ${theme.accent}`,
      boxShadow: `0 0 0 3px ${theme.accent}18`,
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '12px',
    }}>
      <div style={{ background: theme.accent_soft_bg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', fontWeight: '600', background: theme.accent, color: 'white', padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
          FAS {sortOrder + 1}
        </span>
        <input
          autoFocus
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && save()}
          placeholder="Namnge fasen (t.ex. Mobilitet)"
          style={{
            flex: 1, minWidth: '120px', background: 'transparent', border: 'none',
            outline: 'none', fontSize: '13px', fontWeight: '500', color: theme.text_primary,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <span style={{ fontSize: '11px', color: theme.text_secondary }}>Vecka</span>
          <input
            type="number"
            min="1"
            value={startWeek}
            onChange={e => setStartWeek(e.target.value)}
            style={{ width: '40px', padding: '3px 5px', border: `1px solid ${theme.card_border}`, borderRadius: '5px', fontSize: '12px', textAlign: 'center', outline: 'none' }}
          />
          <span style={{ fontSize: '11px', color: theme.text_secondary }}>–</span>
          <input
            type="number"
            min="1"
            value={endWeek}
            onChange={e => setEndWeek(e.target.value)}
            style={{ width: '40px', padding: '3px 5px', border: `1px solid ${theme.card_border}`, borderRadius: '5px', fontSize: '12px', textAlign: 'center', outline: 'none' }}
          />
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: STATUS_COLORS.error, fontSize: '11px', fontWeight: '500', padding: '0 4px' }}
          >
            Ta bort
          </button>
        </div>
      </div>
      <div style={{ background: theme.page_bg, padding: '22px 12px', textAlign: 'center', borderTop: `1px dashed ${theme.card_border}` }}>
        <div style={{ fontSize: '22px', marginBottom: '6px' }}>🤸</div>
        <div style={{ fontSize: '12px', color: theme.text_secondary, marginBottom: '10px' }}>Inga övningar i fasen ännu</div>
        <Button theme={theme} variant="primary" size="sm" onClick={save} disabled={!title.trim() || saving}>
          {saving ? 'Skapar...' : '+ Lägg till övning'}
        </Button>
      </div>
    </div>
  );
}

// ─── Inline-formulär: Tilldela kund ──────────────────────────────────────────
function AssignClientForm({ clients, assignedClientIds, theme, onCancel, onAssigned }) {
  const today = new Date().toISOString().split('T')[0];
  const defaultEnd = new Date();
  defaultEnd.setDate(defaultEnd.getDate() + 56);

  const [clientId, setClientId] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().split('T')[0]);
  const [frequency, setFrequency] = useState('3');

  const availableClients = clients.filter(c => !assignedClientIds.includes(c.id));

  const handleStartChange = (date) => {
    setStartDate(date);
    if (date) {
      const end = new Date(date);
      end.setDate(end.getDate() + 56);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  if (availableClients.length === 0) {
    return (
      <Card theme={theme} style={{ padding: '14px 16px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: theme.text_secondary, fontSize: '0.875em' }}>Alla kunder är redan tilldelade detta program.</span>
        <Button theme={theme} variant="secondary" size="sm" onClick={onCancel}>Stäng</Button>
      </Card>
    );
  }

  return (
    <Card theme={theme} style={{ padding: '16px', marginTop: '10px', marginBottom: '8px' }}>
      <ThemedSelect theme={theme} label="Kund" value={clientId} onChange={(e) => setClientId(e.target.value)}>
        <option value="">Välj kund...</option>
        {availableClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </ThemedSelect>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        <FormInput
          theme={theme}
          type="date"
          label="Startdatum"
          value={startDate}
          onChange={(e) => handleStartChange(e.target.value)}
          style={{ flex: 1, marginBottom: 0 }}
        />
        <FormInput
          theme={theme}
          type="date"
          label="Slutdatum"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{ flex: 1, marginBottom: 0 }}
        />
        <FormInput
          theme={theme}
          type="number"
          label="Pass/vecka"
          min="1"
          max="7"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          style={{ flex: '0 0 100px', marginBottom: 0 }}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          theme={theme}
          variant="primary"
          onClick={() => onAssigned({ clientId, startDate, endDate, frequency: Number(frequency) })}
          disabled={!clientId || !startDate}
        >
          Tilldela program
        </Button>
        <Button theme={theme} variant="secondary" onClick={onCancel}>Avbryt</Button>
      </div>
    </Card>
  );
}

// ─── Hjälp: dostext från exercise-rad ─────────────────────────────────────────
function doseText(pe) {
  const parts = [];
  if (pe.sets) parts.push(`${pe.sets} set`);
  if (pe.reps) parts.push(`${pe.reps} reps`);
  if (pe.duration_seconds) parts.push(`${pe.duration_seconds}s`);
  if (pe.rest_seconds) parts.push(`${pe.rest_seconds}s vila`);
  return parts.join(' · ');
}

// ─── Hjälpfunktioner: datum & status ─────────────────────────────────────────
const MONTHS_SV = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];

function formatShortDate(dateStr) {
  if (!dateStr) return '–';
  const [y, m, d] = dateStr.split('-');
  const month = MONTHS_SV[parseInt(m, 10) - 1];
  const currentYear = new Date().getFullYear();
  if (parseInt(y, 10) === currentYear) return `${parseInt(d, 10)} ${month}`;
  return `${parseInt(d, 10)} ${month} ${y}`;
}

function isActive(endDate) {
  if (!endDate) return true;
  const [y, m, d] = endDate.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end >= today;
}

function clientProgressPct(ac) {
  if (!ac.start_date || !ac.end_date) return null;
  const start = new Date(ac.start_date);
  const end = new Date(ac.end_date);
  const today = new Date();
  if (end <= start) return null;
  const pct = ((today - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

function currentWeekOfProgram(ac, totalWeeks) {
  if (!ac.start_date) return null;
  const start = new Date(ac.start_date);
  const today = new Date();
  const diffMs = today - start;
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
  return Math.min(Math.max(1, diffWeeks), totalWeeks || diffWeeks);
}

// ─── Programdetaljvy ──────────────────────────────────────────────────────────
function ProgramDetail({
  selectedProgram,
  blocks,
  assignedClients,
  clients,
  showAddBlockForm,
  showAssignForm,
  duplicating,
  accentColor = KLINIO_DEFAULT_ACCENT,
  onBack,
  onSaveProgram,
  onShowAddBlockForm,
  onHideAddBlockForm,
  onBlockAdded,
  onSelectBlock,
  onDeleteBlock,
  onRemoveExercise,
  onShowAssignForm,
  onHideAssignForm,
  onAssignClient,
  onUnassignClient,
  onDuplicateProgram,
  onDeleteProgram,
  onNavigateToClient,
}) {
  const theme = resolveTheme(accentColor);
  const suggestedStart = blocks.length > 0 ? Math.max(...blocks.map(b => b.end_week)) + 1 : 1;
  const assignedClientIds = assignedClients.map(ac => ac.client_id);

  // Hero-state: inline redigering av programnamn + beskrivning
  const [localTitle, setLocalTitle] = useState(selectedProgram.title);
  const [localDescription, setLocalDescription] = useState(selectedProgram.description || '');
  const [savingHero, setSavingHero] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editBlockTitle, setEditBlockTitle] = useState('');
  const [editBlockStart, setEditBlockStart] = useState('');
  const [editBlockEnd, setEditBlockEnd] = useState('');
  const [selectedBlockId, setSelectedBlockId] = useState(null);

  const isDirty = localTitle !== selectedProgram.title || localDescription !== (selectedProgram.description || '');

  const saveHero = async () => {
    if (!localTitle.trim()) return;
    setSavingHero(true);
    const { error } = await supabase
      .from('programs')
      .update({ title: localTitle.trim(), description: localDescription.trim() || null })
      .eq('id', selectedProgram.id);
    setSavingHero(false);
    if (error) showToast('Kunde inte spara: ' + error.message, 'error');
    else onSaveProgram({ ...selectedProgram, title: localTitle.trim(), description: localDescription.trim() || null });
  };

  const startEditBlock = (block) => {
    setEditingBlockId(block.id);
    setEditBlockTitle(block.title);
    setEditBlockStart(String(block.start_week));
    setEditBlockEnd(String(block.end_week));
  };

  const saveBlock = async (blockId) => {
    const { error } = await supabase
      .from('program_blocks')
      .update({ title: editBlockTitle, start_week: Number(editBlockStart), end_week: Number(editBlockEnd) })
      .eq('id', blockId);
    if (error) showToast('Kunde inte uppdatera block: ' + error.message, 'error');
    else {
      setEditingBlockId(null);
      onBlockAdded();
    }
  };

  // Metadata-beräkningar
  const maxWeek = blocks.length > 0 ? Math.max(...blocks.map(b => b.end_week)) : null;
  const totalExerciseCount = blocks.reduce((s, b) => s + (Array.isArray(b.program_exercises) ? b.program_exercises.length : 0), 0);
  const heroLabel = !localTitle.trim() ? 'Nytt program' : (isDirty ? 'Ej sparat' : 'Program');


  return (
    <div>
      {/* Tillbaka-länk */}
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: NEUTRAL.muted, fontSize: '13px', fontWeight: '600', marginBottom: '12px', padding: '0', display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}
      >
        ← Alla program
      </button>

      {/* ── Mörkt huvud (T-12) ── */}
      <div className="fr-darkhead" style={{ position: 'relative', overflow: 'hidden', background: NEUTRAL.ink, borderRadius: theme.card_radius, padding: '22px 24px', marginBottom: '16px', color: '#FFFFFF' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '-70px', width: '360px', height: '360px', borderRadius: '999px', background: theme.hero_glow, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />
        <div className="fr-darkhead-inner" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.10em', textTransform: 'uppercase', color: theme.accent_soft_bg, marginBottom: '4px' }}>{heroLabel}</div>
            <input
              value={localTitle}
              onChange={e => setLocalTitle(e.target.value)}
              placeholder="Namnge programmet…"
              aria-label="Programnamn"
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none', fontSize: '24px', fontWeight: '800', color: '#FFFFFF', letterSpacing: '-0.01em', fontFamily: 'var(--font-heading)', padding: 0 }}
            />
            <input
              value={localDescription}
              onChange={e => setLocalDescription(e.target.value)}
              placeholder="Kort beskrivning (valfritt)…"
              aria-label="Beskrivning"
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', color: 'rgba(255,255,255,0.72)', marginTop: '4px', padding: 0, fontFamily: 'inherit' }}
            />
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
              {[
                maxWeek ? `${maxWeek} veckor` : 'Inga faser ännu',
                blocks.length > 0 ? `${blocks.length} ${blocks.length === 1 ? 'fas' : 'faser'}` : null,
                totalExerciseCount > 0 ? `${totalExerciseCount} övningar` : null,
                assignedClients.length > 0 ? `tilldelat ${assignedClients.length} ${assignedClients.length === 1 ? 'kund' : 'kunder'}` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {isDirty && <button type="button" onClick={saveHero} disabled={!localTitle.trim() || savingHero} style={{ ...darkGhost(theme), background: '#FFFFFF', color: NEUTRAL.ink, border: '1px solid #FFFFFF', fontWeight: '600' }}>{savingHero ? 'Sparar…' : 'Spara'}</button>}
            <button type="button" onClick={onShowAssignForm} style={darkPrimary(theme)}>Tilldela kund</button>
          </div>
        </div>
      </div>

      {/* ── Faser (T-12: tidslinje + vald fas) ── */}
      {(() => {
        const sorted = blocks.slice().sort((a, b) => (a.start_week - b.start_week) || (a.sort_order - b.sort_order));
        const current = sorted.find(b => b.id === selectedBlockId) || sorted[0] || null;
        const exercises = current && Array.isArray(current.program_exercises) ? current.program_exercises : [];
        const isEditing = current && editingBlockId === current.id;
        return (
          <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}` }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: NEUTRAL.ink, fontFamily: 'var(--font-heading)' }}>Faser</h2>
              <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Klicka på en fas för att se och ändra dess övningar</span>
            </div>

            {/* Tidslinje */}
            <div className="fr-phase-row" style={{ padding: '14px 20px', display: 'flex', gap: '8px', borderBottom: `1px solid ${theme.card_border}`, flexWrap: 'wrap' }}>
              {sorted.map((block, i) => {
                const isSel = current?.id === block.id;
                const n = Array.isArray(block.program_exercises) ? block.program_exercises.length : 0;
                return (
                  <button
                    key={block.id}
                    type="button"
                    onClick={() => setSelectedBlockId(block.id)}
                    style={{ flex: '2 1 140px', minWidth: 0, textAlign: 'left', borderRadius: '10px', padding: '10px 12px', background: isSel ? theme.accent_soft_bg : NEUTRAL.page, border: `1px solid ${isSel ? theme.accent : theme.card_border}`, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: isSel ? theme.accent_soft_text : NEUTRAL.muted }}>Fas {i + 1} · v {block.start_week}–{block.end_week}</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.title}</div>
                    <div style={{ fontSize: '12px', color: NEUTRAL.text }}>{n} {n === 1 ? 'övning' : 'övningar'}</div>
                  </button>
                );
              })}
              {!showAddBlockForm && (
                <button type="button" onClick={onShowAddBlockForm} style={{ flex: '1 1 80px', minWidth: '80px', borderRadius: '10px', border: `1px dashed ${NEUTRAL.line}`, background: 'transparent', color: theme.accent_text, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', padding: '10px' }}>+ Fas</button>
              )}
            </div>

            {showAddBlockForm && (
              <div style={{ padding: '14px 20px', borderBottom: `1px solid ${theme.card_border}` }}>
                <AddBlockForm
                  programId={selectedProgram.id}
                  sortOrder={blocks.length}
                  suggestedStartWeek={suggestedStart}
                  theme={theme}
                  onCancel={onHideAddBlockForm}
                  onAdded={() => { onBlockAdded(); }}
                />
              </div>
            )}

            {/* Vald fas */}
            {!current ? (
              !showAddBlockForm && (
                <div style={{ margin: '12px', padding: '28px 20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}` }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, marginBottom: '4px' }}>Bygg din första fas</div>
                  <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginBottom: '12px' }}>Ett program består av en eller flera faser. Varje fas har övningar med dosering.</div>
                  <Button theme={theme} variant="primary" size="sm" onClick={onShowAddBlockForm}>+ Lägg till första fasen</Button>
                </div>
              )
            ) : (
              <>
                <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {isEditing ? (
                    <>
                      <input autoFocus value={editBlockTitle} onChange={e => setEditBlockTitle(e.target.value)} placeholder="Namnge fasen" style={{ flex: 1, minWidth: '140px', padding: '7px 10px', borderRadius: '7px', border: `1px solid ${theme.card_border}`, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
                      <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>Vecka</span>
                      <input type="number" min="1" value={editBlockStart} onChange={e => setEditBlockStart(e.target.value)} style={{ width: '48px', padding: '6px', border: `1px solid ${theme.card_border}`, borderRadius: '6px', fontSize: '12px', textAlign: 'center', outline: 'none', fontFamily: 'inherit' }} />
                      <span style={{ fontSize: '12px', color: NEUTRAL.muted }}>–</span>
                      <input type="number" min="1" value={editBlockEnd} onChange={e => setEditBlockEnd(e.target.value)} style={{ width: '48px', padding: '6px', border: `1px solid ${theme.card_border}`, borderRadius: '6px', fontSize: '12px', textAlign: 'center', outline: 'none', fontFamily: 'inherit' }} />
                      <Button theme={theme} variant="primary" size="sm" onClick={() => saveBlock(current.id)}>Spara</Button>
                      <button type="button" onClick={() => setEditingBlockId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: NEUTRAL.muted, fontFamily: 'inherit' }}>Avbryt</button>
                      <button type="button" onClick={() => onDeleteBlock(current.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: STATUS_COLORS.error, fontFamily: 'inherit', marginLeft: 'auto' }}>Ta bort fasen</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted }}>
                        {/^fas\s*\d+/i.test(current.title || '') ? '' : `Fas ${sorted.indexOf(current) + 1} · `}{current.title} · vecka {current.start_week}–{current.end_week}
                      </span>
                      <button type="button" onClick={() => startEditBlock(current)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.accent_text, fontWeight: '600', fontFamily: 'inherit' }}>Redigera fas</button>
                    </>
                  )}
                </div>

                {exercises.length === 0 ? (
                  <div style={{ margin: '0 12px 12px', padding: '20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}` }}>
                    <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginBottom: '10px' }}>Inga övningar i fasen ännu</div>
                    <Button theme={theme} variant="primary" size="sm" onClick={() => onSelectBlock(current)}>+ Lägg till övning</Button>
                  </div>
                ) : (
                  <>
                    {exercises.map(pe => {
                      const ex = pe.exercise || {};
                      return (
                        <div key={pe.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderTop: `1px solid ${theme.card_border}` }}>
                          <span aria-hidden="true" style={{ color: NEUTRAL.faint, fontSize: '12px', width: '16px', letterSpacing: '-2px' }}>⋮⋮</span>
                          <div style={{ width: '36px', height: '28px', borderRadius: '6px', flexShrink: 0, background: ex.video_url ? NEUTRAL.ink : NEUTRAL.line, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {ex.video_url && <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true"><path d="M4 2.5v9l7-4.5z" fill="#FFFFFF" /></svg>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, fontSize: '13px', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: '600' }}>{ex.title || '–'}</span>
                            {(doseText(pe) || ex.category) && <span style={{ color: NEUTRAL.muted }}> · {[doseText(pe), ex.category].filter(Boolean).join(' · ')}</span>}
                          </div>
                          <button type="button" onClick={() => onSelectBlock(current)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.accent_text, fontWeight: '600', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Set &amp; reps</button>
                          <button type="button" aria-label="Ta bort övning" onClick={() => onRemoveExercise(pe.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: NEUTRAL.faint, fontFamily: 'inherit', lineHeight: 1, padding: '4px' }}>×</button>
                        </div>
                      );
                    })}
                    <div style={{ padding: '10px 20px', borderTop: `1px solid ${theme.card_border}` }}>
                      <button type="button" onClick={() => onSelectBlock(current)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.accent_text, fontWeight: '600', fontFamily: 'inherit', padding: 0 }}>+ Lägg till övning</button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* ── Tilldelade kunder-sektion ── */}
      <div style={{ marginBottom: '20px' }}>
        {/* Sektionsrubrik */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '999px', background: theme.accent, flexShrink: 0 }} />
            <span style={{ fontSize: '11px', fontWeight: '600', color: theme.text_secondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Tilldelade kunder · {assignedClients.length}
            </span>
          </div>
          {!showAssignForm && clients.length > 0 && (
            <button onClick={onShowAssignForm}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.accent_text, fontWeight: '500', padding: 0 }}>
              + Tilldela kund
            </button>
          )}
        </div>

        {showAssignForm && (
          <AssignClientForm
            clients={clients}
            assignedClientIds={assignedClientIds}
            theme={theme}
            onCancel={onHideAssignForm}
            onAssigned={onAssignClient}
          />
        )}

        {assignedClients.length === 0 && !showAssignForm ? (
          <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: '12px', padding: '20px' }}>
            <EmptyState
              theme={theme}
              title="Inte tilldelat ännu"
              message={clients.length > 0
                ? 'Tilldela programmet till en kund för att starta rehabiliteringen'
                : 'Lägg till kunder under fliken "Kunder" för att kunna tilldela program.'}
              cta={clients.length > 0 ? (
                <Button theme={theme} variant="primary" onClick={onShowAssignForm}>Tilldela en kund</Button>
              ) : null}
            />
          </div>
        ) : (
          <Card theme={theme} style={{ padding: 0, overflow: 'hidden' }}>
            {assignedClients.map((ac, idx) => {
              const totalWeeks = maxWeek || 8;
              const pct = clientProgressPct(ac);
              const currentWeek = currentWeekOfProgram(ac, totalWeeks);
              const isLast = idx === assignedClients.length - 1;

              return (
                <div key={ac.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                  borderBottom: isLast ? 'none' : `1px solid ${theme.card_border}`,
                }}>
                  <Avatar theme={theme} name={ac.client?.name || '?'} size="sm" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {onNavigateToClient && ac.client ? (
                      <button
                        onClick={() => onNavigateToClient(ac.client)}
                        style={{ background: 'none', border: 'none', padding: 0, fontWeight: '500', fontSize: '13px', color: theme.text_primary, cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.color = theme.accent; e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = theme.text_primary; e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        {ac.client.name}
                      </button>
                    ) : (
                      <span style={{ fontWeight: '500', fontSize: '13px', color: theme.text_primary }}>{ac.client?.name}</span>
                    )}
                    <div style={{ fontSize: '11px', color: theme.text_secondary, marginTop: '2px' }}>
                      {ac.start_date && `Startad ${formatShortDate(ac.start_date)}`}
                      {currentWeek && ` · vecka ${currentWeek} av ${totalWeeks}`}
                      {ac.frequency_per_week && ` · ${ac.frequency_per_week} pass/vecka`}
                    </div>
                  </div>
                  {/* Progress-bar */}
                  {pct !== null && (
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ width: '80px', height: '4px', background: theme.card_border, borderRadius: '999px', overflow: 'hidden', marginBottom: '3px' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: theme.accent, borderRadius: '999px' }} />
                      </div>
                      <div style={{ fontSize: '10px', color: theme.text_secondary }}>{pct}%</div>
                    </div>
                  )}
                  <Badge theme={theme} variant={isActive(ac.end_date) ? 'success' : 'neutral'}>
                    {isActive(ac.end_date) ? 'Aktiv' : 'Avslutad'}
                  </Badge>
                  <button
                    onClick={() => onUnassignClient(ac.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: theme.text_secondary, padding: '2px 4px', borderRadius: '4px' }}
                    onMouseEnter={e => { e.currentTarget.style.color = STATUS_COLORS.error; e.currentTarget.style.background = STATUS_COLORS.error_bg; }}
                    onMouseLeave={e => { e.currentTarget.style.color = theme.text_secondary; e.currentTarget.style.background = 'none'; }}
                  >
                    Ta bort
                  </button>
                </div>
              );
            })}
          </Card>
        )}
      </div>

      {/* ── Bottom actions ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', paddingTop: '16px', borderTop: `1px solid ${theme.card_border}` }}>
        <button
          onClick={() => onDeleteProgram(selectedProgram.id)}
          style={{
            background: 'transparent', cursor: 'pointer',
            color: STATUS_COLORS.error, fontSize: '13px', fontWeight: '500',
            border: `1px solid ${STATUS_COLORS.error}40`,
            borderRadius: theme.btn_radius, padding: '7px 14px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = STATUS_COLORS.error_bg}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Ta bort program
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            theme={theme}
            variant="secondary"
            onClick={() => onDuplicateProgram(selectedProgram)}
            disabled={duplicating}
            loading={duplicating}
          >
            Duplicera program
          </Button>
          <Button theme={theme} variant="secondary" onClick={() => {}}>Exportera PDF</Button>
        </div>
      </div>
    </div>
  );
}

export default ProgramDetail;
